import { useMemo, useState } from 'react'
import { api, session } from '../api.js'
import {
  Banner, Button, Card, ErrorState, Field, Loading, Metric, MetricRow, SectionHeader, StatusPill, Table,
} from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * The safety register — and, now, the place the lifecycle is actually driven from.
 *
 * **This page was read-only, and that was the defect.** Its own comment claimed investigation and closure
 * "happen through the approval flow… on the app", which is not a reason a console cannot do them. Every endpoint
 * involved already admits SAFETY_OFFICER — `transition`, `capa`, `capa/complete`, `capa/verify` — and the Android
 * detail screen wires all four. So Priya, the safety officer PRD §2 describes as living in the incident
 * dashboard, could investigate and close an incident on a phone and not on the computer she would write the
 * investigation up on. Reported as: "once I login as a safety officer, I can see but I should also be able to
 * edit, investigate and close the safety incidents in the web console also".
 *
 * **The server stays the authority on three rules the UI only reflects:**
 *  - `IncidentWorkflow` allows one step at a time (REPORTED → INVESTIGATING → INVESTIGATED → CAPA/VERIFIED →
 *    CLOSED). The screen offers only the legal next steps rather than a free status dropdown.
 *  - VERIFIED and CLOSED are refused while any corrective action is still OPEN. The response carries
 *    `blockedByOpenCapa`, so the button is disabled *with the reason shown* instead of the officer discovering
 *    a 400.
 *  - Closing may require an approval when `ems.approvals.enforce` is on. That surfaces as the server's own
 *    message; the console does not pre-judge it.
 */
export default function Incidents() {
  const list = useApi('/incidents')
  const [openId, setOpenId] = useState(null)
  const [notice, setNotice] = useState(null)

  const rows = useMemo(() => (Array.isArray(list.data) ? list.data : []), [list.data])

  const open = rows.filter((i) => i.status !== 'CLOSED')
  const withInjury = rows.filter((i) => i.injury)
  const blocked = rows.filter((i) => i.blockedByOpenCapa)
  const overdue = rows.filter((i) => (i.capaOverdue || 0) > 0)

  // Layout only — every endpoint re-checks. MANAGEMENT reads the register but is deliberately refused the
  // detail (BE-B11: it carries injury data and reporter identity), so its rows must not be clickable.
  const mayOpenDetail = ['ADMIN', 'SAFETY_OFFICER', 'ELECTRICAL', 'MECHANICAL'].includes(session.role)

  return (
    <>
      <PageHeader
        title="Safety incidents"
        subtitle="Register, investigation and corrective actions. Click an incident to investigate, add CAPA or close it."
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {list.loading && <Loading rows={6} />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}

      {list.data && (
        <>
          <MetricRow>
            <Metric label="Total recorded" value={rows.length} />
            <Metric label="Open" value={open.length} kind={open.length ? 'warning' : 'success'} />
            <Metric
              label="With injury"
              value={withInjury.length}
              kind={withInjury.length ? 'safety' : 'success'}
              hint="Reportable occurrences"
            />
            <Metric
              label="Overdue CAPA"
              value={overdue.reduce((a, i) => a + (i.capaOverdue || 0), 0)}
              kind={overdue.length ? 'danger' : 'success'}
              hint="An open action is an unmitigated risk"
            />
            <Metric
              label="Blocked from closure"
              value={blocked.length}
              hint="Open CAPA prevents closing"
            />
          </MetricRow>

          <SectionHeader hint={mayOpenDetail ? 'Click an incident to open it' : 'Newest first'}>
            Register
          </SectionHeader>
          <Card>
            <Table
              keyOf={(i) => i.id}
              onRowClick={mayOpenDetail ? (i) => setOpenId(i.id) : undefined}
              columns={[
                {
                  key: 'when',
                  header: 'Occurred',
                  render: (i) => fmtDate(i.occurredAt || i.createdAt),
                },
                {
                  key: 'crane',
                  header: 'Crane',
                  render: (i) => (
                    <span className="cell-stack">
                      <strong>{i.assetCode || '—'}</strong>
                      <span className="muted">{[i.department, i.area].filter(Boolean).join(' · ')}</span>
                    </span>
                  ),
                },
                { key: 'type', header: 'Type', render: (i) => title(i.incidentType) },
                {
                  key: 'injury',
                  header: 'Injury',
                  render: (i) => (i.injury
                    ? <StatusPill status="SAFETY" title="Injury reported" />
                    : <span className="muted">No</span>),
                },
                { key: 'severity', header: 'Severity', render: (i) => i.severity ? title(i.severity) : <span className="muted">Not graded</span> },
                { key: 'status', header: 'Status', render: (i) => <StatusPill status={i.status} /> },
                {
                  key: 'what',
                  header: 'Description',
                  render: (i) => <span className="cell-clamp" title={i.description || ''}>{i.description || <span className="muted">None</span>}</span>,
                },
                {
                  key: 'capa',
                  header: 'CAPA',
                  numeric: true,
                  render: (i) => {
                    const total = i.capaTotal || 0
                    if (!total) return <span className="muted">None</span>
                    const od = i.capaOverdue || 0
                    return (
                      <span className={od ? 'val-bad' : undefined} title={od ? `${od} past due` : undefined}>
                        {i.capaOpen || 0}/{total} open{od ? ` · ${od} late` : ''}
                      </span>
                    )
                  },
                },
                { key: 'by', header: 'Reported by', render: (i) => i.reportedByName || <span className="muted">—</span> },
              ]}
              rows={rows}
              empty={<p className="muted">No incidents recorded. That is the desired state.</p>}
            />
          </Card>

          {rows.some((i) => !i.severity) && (
            <p className="muted" style={{ marginTop: 'var(--md)' }}>
              Ungraded severity is expected on operator reports: the person who has just witnessed a near miss
              is the worst placed to grade it, so the safety officer sets it during investigation.
            </p>
          )}
        </>
      )}

      {openId && (
        <IncidentDetail
          incidentId={openId}
          onClose={() => setOpenId(null)}
          onChanged={(text) => {
            // Reload the register, not just the sheet: a transition changes the status, the CAPA counts and
            // `blockedByOpenCapa` on the row behind it, and a stale row is how a closed incident keeps
            // showing as open until someone refreshes the page.
            list.reload()
            setNotice({ kind: 'success', text })
          }}
        />
      )}
    </>
  )
}

/* ---- One incident: investigate, CAPA, close ------------------------------ */

function IncidentDetail({ incidentId, onClose, onChanged }) {
  const detail = useApi(`/incidents/${incidentId}`)
  const capa = useApi(`/incidents/${incidentId}/capa`)
  const evidence = useApi(`/incidents/${incidentId}/attachments`)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [advancing, setAdvancing] = useState(null)   // the status being advanced to
  const [rootCause, setRootCause] = useState('')
  const [addingCapa, setAddingCapa] = useState(false)

  const i = detail.data
  const actions = Array.isArray(capa.data) ? capa.data : []
  const photos = Array.isArray(evidence.data) ? evidence.data : []

  async function run(fn, text) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      detail.reload()
      capa.reload()
      onChanged(text)
    } catch (e) {
      // The server's own words. "Something went wrong" would hide the two messages that matter most here:
      // the open-CAPA refusal and the approval requirement.
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  // Only the legal next steps, from IncidentWorkflow. A free status dropdown would offer illegal jumps and
  // teach the officer that the app rejects things at random.
  const next = i ? (NEXT[i.status] || []) : []

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet sheet-wide" onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
          <h2>{i ? `${title(i.incidentType)} · ${i.assetCode || 'crane'}` : 'Incident'}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          {detail.loading && <Loading rows={6} />}
          {detail.error && <ErrorState error={detail.error} onRetry={detail.reload} />}
          {error && <Banner kind="danger">{error}</Banner>}

          {i && (
            <>
              {/* Safety first: an injury outranks everything else on this sheet. */}
              {i.injury && (
                <Banner kind="danger">
                  <span><strong>Injury reported.</strong> This is a reportable occurrence.</span>
                </Banner>
              )}

              <div className="grid-2">
                <KeyValue label="Status" value={<StatusPill status={i.status} />} />
                <KeyValue label="Severity" value={i.severity ? title(i.severity) : <span className="muted">Not graded</span>} />
                <KeyValue label="Crane" value={`${i.assetCode || '—'}${i.assetName ? ` — ${i.assetName}` : ''}`} />
                <KeyValue label="Location" value={[i.department, i.area].filter(Boolean).join(' · ') || '—'} />
                <KeyValue label="Occurred" value={fmtDate(i.occurredAt || i.createdAt)} />
                <KeyValue label="Reported by" value={i.reportedByName || '—'} />
                {i.closedAt && <KeyValue label="Closed" value={fmtDate(i.closedAt)} />}
              </div>

              <SectionHeader>What happened</SectionHeader>
              <p>{i.description || <span className="muted">No description was recorded.</span>}</p>

              {i.rootCause && (
                <>
                  <SectionHeader>Root cause</SectionHeader>
                  <p>{i.rootCause}</p>
                </>
              )}

              {/* ---- Lifecycle ---- */}
              <SectionHeader hint="One step at a time, as the workflow allows">Investigation</SectionHeader>

              {i.status === 'CLOSED' ? (
                <Banner kind="success">
                  This incident is closed. Correcting a closed record means raising a new incident — the
                  register is an audit trail, not a document.
                </Banner>
              ) : (
                <>
                  {i.blockedByOpenCapa && (
                    <Banner kind="warning">
                      <span>
                        <strong>{i.capaOpen} corrective action(s) still open.</strong> An incident cannot be
                        verified or closed while a corrective action is outstanding — the hazard has not been
                        dealt with yet.
                      </span>
                    </Banner>
                  )}

                  <Timeline current={i.status} />

                  <div className="cell-row" style={{ gap: 'var(--sm)', flexWrap: 'wrap' }}>
                    {next.map((to) => {
                      // VERIFIED and CLOSED are what the open-CAPA rule blocks; INVESTIGATING and INVESTIGATED
                      // are not, so they stay enabled.
                      const gated = (to === 'VERIFIED' || to === 'CLOSED') && i.blockedByOpenCapa
                      return (
                        <Button
                          key={to}
                          variant={to === 'CLOSED' ? 'primary' : 'secondary'}
                          disabled={busy || gated}
                          title={gated ? 'Complete the open corrective actions first' : undefined}
                          onClick={() => { setAdvancing(to); setRootCause(i.rootCause || '') }}
                        >
                          {ADVANCE_LABEL[to] || `Move to ${title(to)}`}
                        </Button>
                      )
                    })}
                    {next.length === 0 && (
                      <p className="muted">No further step is available from {title(i.status)}.</p>
                    )}
                  </div>

                  {advancing && (
                    <Card>
                      <SectionHeader>{ADVANCE_LABEL[advancing] || `Move to ${title(advancing)}`}</SectionHeader>
                      {/* Root cause is offered on the steps where an investigator has actually concluded
                          something. It is optional on the server, so it is optional here — but INVESTIGATED
                          with no root cause is a finding nobody can act on, so the hint says so. */}
                      {(advancing === 'INVESTIGATED' || advancing === 'VERIFIED' || advancing === 'CLOSED') && (
                        <Field
                          label="Root cause"
                          id="rc"
                          hint="What actually caused this. Optional on the server, but an investigation without one explains nothing."
                        >
                          <textarea id="rc" rows={3} value={rootCause} onChange={(e) => setRootCause(e.target.value)} />
                        </Field>
                      )}
                      {advancing === 'CLOSED' && (
                        <Banner kind="info">
                          Closing is final and may require an approval, depending on plant configuration. If it
                          does, the server will say so and the request will wait for a decision.
                        </Banner>
                      )}
                      <div className="cell-row" style={{ gap: 'var(--sm)' }}>
                        <Button variant="secondary" onClick={() => setAdvancing(null)}>Cancel</Button>
                        <Button
                          disabled={busy}
                          onClick={() => {
                            const to = advancing
                            setAdvancing(null)
                            run(
                              () => api(`/incidents/${incidentId}/transition`, {
                                method: 'POST',
                                body: { toStatus: to, rootCause: rootCause.trim() || null },
                              }),
                              `Incident moved to ${title(to)}.`,
                            )
                          }}
                        >
                          {busy ? 'Saving…' : 'Confirm'}
                        </Button>
                      </div>
                    </Card>
                  )}
                </>
              )}

              {/* ---- CAPA ---- */}
              <SectionHeader hint="Corrective and preventive actions, with an owner and a due date">
                Corrective actions
              </SectionHeader>

              {capa.loading && <Loading rows={2} />}
              {capa.data && actions.length === 0 && (
                <p className="muted">No corrective actions yet.</p>
              )}
              {actions.length > 0 && (
                <Table
                  keyOf={(c) => c.id}
                  columns={[
                    { key: 'type', header: 'Type', render: (c) => title(c.type) },
                    { key: 'what', header: 'Action', render: (c) => c.description },
                    { key: 'owner', header: 'Owner', render: (c) => c.ownerName || <span className="muted">Unassigned</span> },
                    {
                      key: 'due',
                      header: 'Due',
                      render: (c) => (c.dueAt
                        ? <span className={c.overdue ? 'val-bad' : undefined}>{fmtDate(c.dueAt)}</span>
                        : <span className="muted">No date</span>),
                    },
                    { key: 'status', header: 'Status', render: (c) => <StatusPill status={c.overdue && c.status === 'OPEN' ? 'OVERDUE' : c.status} /> },
                    {
                      key: 'who',
                      header: 'Signed',
                      render: (c) => (
                        <span className="cell-stack">
                          {c.completedByName && <span>Done: {c.completedByName}</span>}
                          {c.verifiedByName && <span>Verified: {c.verifiedByName}</span>}
                          {!c.completedByName && !c.verifiedByName && <span className="muted">—</span>}
                        </span>
                      ),
                    },
                    {
                      key: 'act',
                      header: '',
                      render: (c) => {
                        if (c.status === 'OPEN') {
                          return (
                            <Button
                              variant="secondary"
                              disabled={busy}
                              onClick={() => run(
                                () => api(`/incidents/capa/${c.id}/complete`, { method: 'POST', body: {} }),
                                'Corrective action marked done.',
                              )}
                            >
                              Mark done
                            </Button>
                          )
                        }
                        if (c.status === 'DONE') {
                          // The server refuses self-verification — the point of the step is a second pair of
                          // eyes. Saying so on a disabled button beats a 400 after the click.
                          const ownWork = c.completedBy && c.completedBy === myUserId()
                          return (
                            <Button
                              variant="secondary"
                              disabled={busy || ownWork}
                              title={ownWork ? 'Someone other than the person who did the work must verify it' : undefined}
                              onClick={() => run(
                                () => api(`/incidents/capa/${c.id}/verify`, { method: 'POST', body: {} }),
                                'Corrective action verified.',
                              )}
                            >
                              Verify
                            </Button>
                          )
                        }
                        return <span className="muted">Complete</span>
                      },
                    },
                  ]}
                  rows={actions}
                />
              )}

              {i.status !== 'CLOSED' && (
                addingCapa
                  ? <CapaForm
                      busy={busy}
                      onCancel={() => setAddingCapa(false)}
                      onSave={(body) => {
                        setAddingCapa(false)
                        run(
                          () => api(`/incidents/${incidentId}/capa`, { method: 'POST', body }),
                          'Corrective action added.',
                        )
                      }}
                    />
                  : <Button variant="secondary" onClick={() => setAddingCapa(true)}>Add corrective action</Button>
              )}

              {/* ---- Evidence ---- */}
              {photos.length > 0 && (
                <>
                  <SectionHeader hint="Captured at the scene">Evidence</SectionHeader>
                  <div className="cell-row" style={{ gap: 'var(--sm)', flexWrap: 'wrap' }}>
                    {photos.map((p) => (
                      // Signed, time-limited URLs from the server — never a raw storage key.
                      <a key={p.id} href={p.downloadUrl} target="_blank" rel="noreferrer" className="btn">
                        {title(p.kind)}{p.isLiveCapture ? ' (live)' : ''} · {fmtDate(p.capturedAt || p.createdAt)}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ---- Add a corrective action -------------------------------------------- */

function CapaForm({ busy, onCancel, onSave }) {
  // `/users/assignable` rather than `/admin/users`: that endpoint is ADMIN-only and returns mobile numbers and
  // email addresses, and a safety officer naming an owner needs to identify a colleague, not read their contact
  // details. Added for this form.
  const people = useApi('/users/assignable')
  const staff = Array.isArray(people.data) ? people.data : []

  const [f, setF] = useState({ type: 'CORRECTIVE', description: '', ownerUserId: '', dueDays: '14' })
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  return (
    <Card>
      <SectionHeader>New corrective action</SectionHeader>
      <div className="grid-2">
        <Field label="Type" id="ct" hint="Corrective fixes this; preventive stops it recurring">
          <select id="ct" value={f.type} onChange={set('type')}>
            <option value="CORRECTIVE">Corrective</option>
            <option value="PREVENTIVE">Preventive</option>
          </select>
        </Field>
        <Field
          label="Owner"
          id="co"
          hint={people.error ? 'Could not load staff — the action can still be saved unassigned' : 'Who is accountable for doing it'}
        >
          <select id="co" value={f.ownerUserId} onChange={set('ownerUserId')}>
            <option value="">Unassigned</option>
            {staff.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.name} — {title(u.role)}{u.discipline ? ` (${title(u.discipline)})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Due in (days)" id="cd" hint="Leave blank for no date; an action with no date is never overdue">
          <input id="cd" type="number" min="1" value={f.dueDays} onChange={set('dueDays')} />
        </Field>
      </div>
      <Field label="What must be done" id="cw">
        <textarea id="cw" rows={3} value={f.description} onChange={set('description')} />
      </Field>
      <div className="cell-row" style={{ gap: 'var(--sm)' }}>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button
          disabled={busy || !f.description.trim()}
          onClick={() => onSave({
            type: f.type,
            description: f.description.trim(),
            ownerUserId: f.ownerUserId || null,
            // Days-from-now converted here, matching the Android form: asking anyone to type an ISO timestamp
            // is how due dates end up wrong or absent.
            dueAt: f.dueDays && Number(f.dueDays) > 0
              ? new Date(Date.now() + Number(f.dueDays) * 86400000).toISOString()
              : null,
          })}
        >
          {busy ? 'Saving…' : 'Add action'}
        </Button>
      </div>
    </Card>
  )
}

/* ---- Bits ---------------------------------------------------------------- */

/**
 * The lifecycle, with the current step marked.
 *
 * Shown because "what comes next" is not obvious from a status word alone, and because the officer needs to see
 * that CLOSED is four steps from REPORTED rather than one — the reason a free status dropdown would be wrong.
 */
function Timeline({ current }) {
  // The server's own status order (`IncidentWorkflow`), labelled as stages a person recognises: "CAPA" is the
  // stage where corrective actions are outstanding, which "Capa" did not convey.
  const stages = [
    ['REPORTED', 'Reported'],
    ['INVESTIGATING', 'Investigating'],
    ['INVESTIGATED', 'Findings recorded'],
    ['CAPA', 'CAPA outstanding'],
    ['VERIFIED', 'Verified'],
    ['CLOSED', 'Closed'],
  ]
  const at = stages.findIndex(([s]) => s === current)
  return (
    <div className="cell-row" style={{ gap: 'var(--xs)', flexWrap: 'wrap', marginBottom: 'var(--sm)' }}>
      {stages.map(([s, label], idx) => (
        <span
          key={s}
          className={idx === at ? undefined : 'muted'}
          title={idx < at ? 'Done' : idx === at ? 'Current' : 'Not yet'}
        >
          {idx === at ? <strong>{label}</strong> : label}
          {idx < stages.length - 1 && <span className="muted"> → </span>}
        </span>
      ))}
    </div>
  )
}

function KeyValue({ label, value }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: '0.85em' }}>{label}</div>
      <div>{value}</div>
    </div>
  )
}

/** The legal next steps, mirroring `IncidentWorkflow.allowed` on the server. */
const NEXT = {
  REPORTED: ['INVESTIGATING'],
  INVESTIGATING: ['INVESTIGATED'],
  INVESTIGATED: ['CAPA', 'VERIFIED'],
  CAPA: ['VERIFIED'],
  VERIFIED: ['CLOSED'],
  CLOSED: [],
}

/** Verbs, not status names: "Start investigation" is an action, "Move to Investigating" is a state machine. */
const ADVANCE_LABEL = {
  INVESTIGATING: 'Start investigation',
  INVESTIGATED: 'Record findings',
  CAPA: 'Raise corrective actions',
  VERIFIED: 'Verify effectiveness',
  CLOSED: 'Close incident',
}

/** From the stored JWT claims, for the self-verification check. Layout only — the server enforces it. */
function myUserId() {
  try {
    const t = session.token
    if (!t) return null
    const p = t.split('.')[1]
    const json = atob(p.replace(/-/g, '+').replace(/_/g, '/').padEnd(p.length + ((4 - (p.length % 4)) % 4), '='))
    return JSON.parse(json).sub || null
  } catch {
    return null
  }
}

/**
 * Wire enum → readable label, with acronyms preserved.
 *
 * Plain title-casing turned the `CAPA` lifecycle stage into "Capa" in the timeline, which is not a word and is
 * not what the safety department calls it. The same helper is used for roles and CAPA types, so the exception
 * list stays here rather than being special-cased at each call site.
 */
const ACRONYMS = { CAPA: 'CAPA', AH: 'AH', MH: 'MH', LOTO: 'LOTO', SLA: 'SLA', PM: 'PM' }
const title = (s) => (s
  ? s.split('_').map((w) => ACRONYMS[w] || w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
  : '—')

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
